import React, { useEffect, useMemo, useState } from 'react';
import { Mouth } from '../Interfaces/Mouth.interface';
import { useEffectAsync } from '../../../helpers';
import useLoading from '../../Base/Hooks/useLoading';
import {
	MedicalExaminationState,
	Tooth,
	UpdateToothInput,
	useDentMedicalExaminationsQuery,
	useLazyGetDentitionByUserIdQuery,
	useUpdateToothBulkMutation,
	useUpdateToothMutation,
} from '../../../graphql/graphql';
import useApiClient from '../../Base/Hooks/useApiClient';
import { OverviewMode } from '../Enums/OverviewMode.enum';
import { ToothDetailContentType } from '../Enums/ToothDetailContentType.enum';
import { buildMouth, createToothMap } from './DENTMouth.service';
import useNotification from '../../../Hooks/useNotification.hook';
import { PeriodontalSurface } from '../Interfaces/PeriodontalObject.interface';
import { TOOTH_NUMBER_ORDER } from '../Helpers/toothNumberOrder';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';

type UpdateToothValues = Omit<UpdateToothInput, 'medicalExaminationId'>;

export interface DENTContextProps {
	mouthData?: Mouth;
	activeTooth?: Tooth;
	setActiveTooth: React.Dispatch<React.SetStateAction<Tooth | undefined>>;
	reloadMouthData: () => void;
	overviewMode?: OverviewMode;
	setOverviewMode: (overviewMode: OverviewMode) => void;
	handleDiscardTooth: () => void;
	handleUpdateTooth: (data: UpdateToothValues) => Promise<void>;
	activePeriodontalItem: PeriodontalSurface;
	setActivePeriodontalItem: (type: PeriodontalSurface) => void;
	originalActiveTooth?: Tooth;
	quickSelectTeeth: Tooth[];
	setQuickSelectTeeth: (teeth: Tooth[]) => void;
	toggleQuickSelectTeeth: (tooth: Tooth) => void;
	isToothQuickSelected: (id: string) => boolean;
	handleUpdateToothBulk: (data: UpdateToothInput) => void;
	quickSelectMode: boolean;
	setQuickSelectMode: (value: boolean) => void;
	adjustmentMode: boolean;
	setAdjustmentMode: (value: boolean) => void;
	showEndodontic: boolean;
	setShowEndodontic: React.Dispatch<React.SetStateAction<boolean>>;
	showPeriodontal: boolean;
	setShowPeriodontal: React.Dispatch<React.SetStateAction<boolean>>;
	showDental: boolean;
	setShowDental: React.Dispatch<React.SetStateAction<boolean>>;
	toothMap?: Map<number, Tooth>;
	selectNextTooth: () => void;
	toothDetailContentType: ToothDetailContentType;
	setToothDetailContentType: (type: ToothDetailContentType) => void;
	hideMiddlePeriodontalItems: boolean;
	dentitionId?: string;
	activeMedicalExaminationId?: string;
	setActiveMedicalExaminationId: React.Dispatch<
		React.SetStateAction<string | undefined>
	>;
	medicalExaminationNotificationShown: boolean;
	setMedicalExaminationNotificationShown: (shown: boolean) => void;
	patientId: string;
}

export interface DENTContextProviderProps {
	children?: React.ReactChild | React.ReactChild[];
	patientId: string;
}

const noopToothSetter: React.Dispatch<
	React.SetStateAction<Tooth | undefined>
> = () => {};

export const DENTContext = React.createContext<DENTContextProps>({
	setActiveTooth: noopToothSetter,
	reloadMouthData: () => {},
	setOverviewMode: () => {},
	handleDiscardTooth: () => {},
	handleUpdateTooth: async () => {},
	activePeriodontalItem: 'buccal',
	setActivePeriodontalItem: () => {},
	quickSelectTeeth: [],
	setQuickSelectTeeth: () => {},
	toggleQuickSelectTeeth: () => {},
	isToothQuickSelected: () => false,
	handleUpdateToothBulk: () => {},
	quickSelectMode: false,
	setQuickSelectMode: () => {},
	adjustmentMode: false,
	setAdjustmentMode: () => {},
	showEndodontic: true,
	setShowEndodontic: () => {},
	showPeriodontal: true,
	setShowPeriodontal: () => {},
	showDental: true,
	setShowDental: () => {},
	selectNextTooth: () => {},
	toothDetailContentType: ToothDetailContentType.GENERAL,
	setToothDetailContentType: () => {},
	hideMiddlePeriodontalItems: false,
	setActiveMedicalExaminationId: () => {},
	medicalExaminationNotificationShown: false,
	setMedicalExaminationNotificationShown: () => {},
	patientId: '',
});

export const useDENTContext: () => DENTContextProps = () => {
	return React.useContext<DENTContextProps>(DENTContext);
};

export function DENTContextProvider({
	children,
	patientId,
}: DENTContextProviderProps) {
	const { displayError, displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { isLoading, RenderLoading, setIsLoading } = useLoading();
	const [updateTooth] = useUpdateToothMutation();
	const [getDentition] = useLazyGetDentitionByUserIdQuery();
	const [activeTooth, setActiveTooth] = useState<Tooth | undefined>();
	const [activePeriodontalItem, setActivePeriodontalItem] =
		useState<PeriodontalSurface>('buccal');
	const [overviewMode, setOverviewMode] = useState<OverviewMode>(
		OverviewMode.DEFAULT,
	);
	const [toothMap, setToothMap] = useState<Map<number, Tooth> | undefined>();
	const [quickSelectTeeth, setQuickSelectTeeth] = useState<Tooth[]>([]);
	const [updateToothBulk] = useUpdateToothBulkMutation();
	const [quickSelectMode, setQuickSelectMode] = useState(false);
	const [adjustmentMode, setAdjustmentMode] = useState(false);
	const [showEndodontic, setShowEndodontic] = useState(true);
	const [showPeriodontal, setShowPeriodontal] = useState(true);
	const [showDental, setShowDental] = useState(true);
	const [toothDetailContentType, setToothDetailContentType] =
		useState<ToothDetailContentType>(ToothDetailContentType.GENERAL);
	const { healthPlanWorkplace } = useHealthPlanContext();
	const [dentitionId, setDentitionId] = useState<string>();
	const [
		medicalExaminationNotificationShown,
		setMedicalExaminationNotificationShown,
	] = useState(false);
	const [activeMedicalExaminationId, setActiveMedicalExaminationId] =
		useState<string>();
	const { data: medicalExaminationsData, refetch: refetchMedicalExaminations } =
		useDentMedicalExaminationsQuery({
			where: { patientId, state: MedicalExaminationState.Active },
		});

	const mouthData = useMemo(() => {
		if (toothMap) return buildMouth(toothMap);
		return undefined;
	}, [toothMap]);

	const hideMiddlePeriodontalItems = useMemo(
		() => healthPlanWorkplace?.settings?.periodontalItemsCount === 4,
		[healthPlanWorkplace],
	);

	useEffect(() => {
		setActiveMedicalExaminationId(
			medicalExaminationsData?.medicalExaminations?.data[0]?.id,
		);
	}, [medicalExaminationsData]);

	//FC TO LOAD DATA FROM API AND BUILD INTO MOUTH OBJECT
	const reloadMouthData = async () => {
		setIsLoading(true);
		try {
			const response = await getDentition({
				where: {
					userId: patientId,
				},
			});

			displayGQLError(response);
			if ('data' in response && response.data?.dentitionByUserId) {
				const dentition = response.data.dentitionByUserId;

				const dbTeeth = dentition.teeth as Tooth[];
				setToothMap(createToothMap(dbTeeth));
				setDentitionId(dentition.id);

				if (activeTooth) {
					setActiveTooth(dbTeeth.find(tooth => tooth.id === activeTooth.id));
				}
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const originalActiveTooth = useMemo(() => {
		if (activeTooth?.number && toothMap) {
			return toothMap.get(activeTooth.number);
		}

		return;
	}, [activeTooth, toothMap]);

	//RUN DATA LOADING ON CONTEXT INIT
	useEffectAsync(async () => {
		await reloadMouthData();
	}, []);

	useEffect(() => {
		if (
			overviewMode === OverviewMode.DEFAULT ||
			overviewMode === OverviewMode.PERIODONTAL ||
			overviewMode === OverviewMode.PATHOLOGY ||
			overviewMode === OverviewMode.RESTORATION
		) {
			setQuickSelectTeeth([]);
			setQuickSelectMode(false);
		}
	}, [overviewMode]);

	const handleDiscardTooth = () => {
		setActiveTooth(undefined);
	};

	const sanitizeToothData = (data: UpdateToothValues) => {
		if (data.pathology?.decays) {
			const validDecays = data.pathology.decays.filter(
				decay => Object.keys(decay).length > 0 && decay.surfaces.length > 0,
			);

			if (validDecays.length === 0) {
				delete data.pathology.decays;
			} else {
				data.pathology.decays = validDecays;
			}
		}

		return data;
	};

	const handleUpdateTooth = async (data: UpdateToothValues) => {
		if (!(activeTooth && activeMedicalExaminationId)) return;

		const result = await updateTooth({
			toothId: activeTooth.id,
			input: {
				...sanitizeToothData(data),
				medicalExaminationId: activeMedicalExaminationId,
			},
		});

		if ('error' in result) {
			displayGQLError(result);
		} else {
			showNotificationSuccess({
				content: 'patientTreatment.meddiDENT.toothUpdated',
			});

			await reloadMouthData();
		}
	};

	const handleUpdateToothBulk = async (data: UpdateToothValues) => {
		if (!(quickSelectTeeth.length && activeMedicalExaminationId)) return;

		const result = await updateToothBulk({
			where: {
				ids: quickSelectTeeth.map(tooth => tooth.id),
			},
			input: {
				...sanitizeToothData(data),
				medicalExaminationId: activeMedicalExaminationId,
			},
		});

		if ('error' in result) {
			displayGQLError(result);
		} else {
			showNotificationSuccess({
				content: 'patientTreatment.meddiDENT.toothUpdated',
			});
			await reloadMouthData();
		}

		setActiveTooth(undefined);
		setQuickSelectTeeth([]);
	};

	const toggleQuickSelectTeeth = (tooth: Tooth) => {
		if (isToothQuickSelected(tooth.id)) {
			setQuickSelectTeeth(quickSelectTeeth.filter(t => t.id !== tooth.id));
		} else {
			setQuickSelectTeeth([...quickSelectTeeth, tooth]);
		}
	};

	const isToothQuickSelected = (id: string) => {
		return quickSelectTeeth.map(tooth => tooth.id).includes(id);
	};

	const selectNextTooth = () => {
		if (!(activeTooth && toothMap)) return;

		const activeToothIndex = TOOTH_NUMBER_ORDER.indexOf(activeTooth.number);
		const nextToothIndex =
			activeToothIndex === TOOTH_NUMBER_ORDER.length - 1
				? 0
				: activeToothIndex + 1;

		setActiveTooth(toothMap.get(TOOTH_NUMBER_ORDER[nextToothIndex]));
	};

	return (
		<DENTContext.Provider
			value={{
				mouthData,
				activeTooth,
				setActiveTooth,
				reloadMouthData,
				overviewMode,
				setOverviewMode,
				handleDiscardTooth,
				handleUpdateTooth,
				handleUpdateToothBulk,
				activePeriodontalItem,
				setActivePeriodontalItem,
				originalActiveTooth,
				quickSelectTeeth,
				setQuickSelectTeeth,
				toggleQuickSelectTeeth,
				isToothQuickSelected,
				quickSelectMode,
				setQuickSelectMode,
				adjustmentMode,
				setAdjustmentMode,
				showEndodontic,
				setShowEndodontic,
				showPeriodontal,
				setShowPeriodontal,
				showDental,
				setShowDental,
				toothMap,
				selectNextTooth,
				toothDetailContentType,
				setToothDetailContentType,
				hideMiddlePeriodontalItems,
				dentitionId,
				activeMedicalExaminationId,
				setActiveMedicalExaminationId,
				medicalExaminationNotificationShown,
				setMedicalExaminationNotificationShown,
				patientId,
			}}>
			{isLoading && <RenderLoading />}
			{!isLoading && children}
		</DENTContext.Provider>
	);
}
